import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { DeviceTokenService } from './device-token.service';
import { DeviceTokenResponse } from '../domain/device-token';
import { NativePushService, NativePushStatus } from './native-push.service';

export interface PushNotificationConfig {
  vapidPublicKey: string;
  backendUrl: string;
  subscribeEndpoint?: string;
  sendNotificationEndpoint?: string;
}

export interface SubscriptionStatus {
  isSubscribed: boolean;
  subscription: PushSubscription | null;
  permission: NotificationPermission;
  error?: string;
  serviceWorkerReady?: boolean;
  vapidConfigured?: boolean;
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface PushNotificationResult {
  success: boolean;
  subscription?: PushSubscriptionData;
  deviceToken?: DeviceTokenResponse;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private http = inject(HttpClient);
  private deviceTokenService = inject(DeviceTokenService);
  private nativePushService = inject(NativePushService);
  private translate = inject(TranslateService);
  private config!: PushNotificationConfig;
  private readonly isNative = Capacitor.isNativePlatform();
  private nativeStatusBound = false;
  private subscriptionStatus = new BehaviorSubject<SubscriptionStatus>({
    isSubscribed: false,
    subscription: null,
    permission: 'default',
    serviceWorkerReady: false,
    vapidConfigured: false
  });
  public subscriptionStatus$ = this.subscriptionStatus.asObservable();

  constructor() {
    // Subscribe to language changes and update service worker
    this.translate.onLangChange.subscribe(event => {
      console.log(`[PushNotificationService] Language changed to: ${event.lang}`);
      this.setServiceWorkerLanguage(event.lang);
    });
    
    // Set initial language when service starts
    const currentLang = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.setServiceWorkerLanguage(currentLang);
  }

  /**
   * Initialize the push notification service with configuration
   */
  initialize(config?: PushNotificationConfig): void {
    // On the native Capacitor shell, push is handled by the FCM-backed
    // NativePushService (device_type: 'android'), not the browser Web
    // Push/VAPID stack. Mirror its status into the web-shaped SubscriptionStatus
    // so the shared notification-settings UI keeps working unchanged.
    if (this.isNative) {
      this.bindNativeStatus();
      void this.nativePushService.init();
      return;
    }

    // Use environment config if no config provided
    this.config = config || {
      vapidPublicKey: environment.vapidPublicKey,
      backendUrl: environment.apiUrl,
      subscribeEndpoint: '/device-tokens',
      sendNotificationEndpoint: '/push/test'
    };    if (!this.config.vapidPublicKey) {
      console.error('VAPID public key not configured in environment');
      this.updateStatus({
        isSubscribed: false,
        subscription: null,
        permission: 'default',
        error: 'VAPID public key not configured',
        serviceWorkerReady: false,
        vapidConfigured: false
      });
      return;
    }

    this.checkCurrentSubscription();
  }

  /**
   * Check current subscription status
   */
  private async checkCurrentSubscription(): Promise<void> {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        this.updateStatus({
          isSubscribed: false,
          subscription: null,
          permission: 'denied',
          error: 'Push notifications not supported',
          serviceWorkerReady: false,
          vapidConfigured: !!this.config?.vapidPublicKey
        });
        return;
      }

      // Register service worker if needed
      await this.registerServiceWorker();

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      this.updateStatus({
        isSubscribed: !!subscription,
        subscription: subscription,
        permission: Notification.permission,
        serviceWorkerReady: true,
        vapidConfigured: !!this.config?.vapidPublicKey
      });

    } catch (error) {
      console.error('Error checking subscription:', error);
      this.updateStatus({
        isSubscribed: false,
        subscription: null,
        permission: Notification.permission,
        error: 'Error checking subscription status',
        serviceWorkerReady: false,
        vapidConfigured: !!this.config?.vapidPublicKey
      });
    }
  }

  /**
   * Register service worker for push notifications
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/push-sw.js', {
        scope: '/'
      });
      
      console.log('Push Service Worker registered:', registration.scope);
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Push Service Worker is ready');
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }
  /**
   * Request notification permission and subscribe
   */
  async requestPermissionAndSubscribe(): Promise<PushNotificationResult> {
    if (this.isNative) {
      this.bindNativeStatus();
      const result = await this.nativePushService.enable();
      return { success: result.success, error: result.error };
    }
    try {
      if (!this.config) {
        await this.initialize();
      }

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.updateStatus({
          isSubscribed: false,
          subscription: null,
          permission: permission,
          error: 'Notification permission denied',
          serviceWorkerReady: false,
          vapidConfigured: !!this.config?.vapidPublicKey
        });
        return { success: false, error: 'Notification permission denied' };
      }

      // Subscribe to push notifications
      const registration = await navigator.serviceWorker.ready;
      const convertedVapidKey = this.urlBase64ToUint8Array(this.config.vapidPublicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      // Extract subscription data
      const subscriptionData = this.extractSubscriptionData(subscription);

      // Send subscription to backend using DeviceTokenService
      const deviceTokenResult = await this.sendSubscriptionToBackend(subscriptionData);

      this.updateStatus({
        isSubscribed: true,
        subscription: subscription,
        permission: permission,
        serviceWorkerReady: true,
        vapidConfigured: true
      });

      console.log('Successfully subscribed to push notifications');
      return { 
        success: true, 
        subscription: subscriptionData,
        deviceToken: deviceTokenResult.device_token
      };

    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      this.updateStatus({
        isSubscribed: false,
        subscription: null,
        permission: Notification.permission,
        error: `Subscription failed: ${error}`,
        serviceWorkerReady: false,
        vapidConfigured: !!this.config?.vapidPublicKey
      });
      return { 
        success: false,        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Extract subscription data in format expected by backend
   */
  private extractSubscriptionData(subscription: PushSubscription): PushSubscriptionData {
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');

    if (!p256dhKey || !authKey) {
      throw new Error('Unable to get subscription keys');
    }

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(p256dhKey),
        auth: this.arrayBufferToBase64(authKey)
      }
    };
  }

  /**
   * Send subscription to backend using DeviceTokenService
   */
  private async sendSubscriptionToBackend(subscription: PushSubscriptionData): Promise<{ success: boolean; device_token?: DeviceTokenResponse; error?: string }> {
    try {
      const deviceTokenRequest = this.deviceTokenService.createDeviceTokenFromSubscription(
        subscription.endpoint,
        subscription.keys.p256dh,
        subscription.keys.auth
      );

      const result = await firstValueFrom(this.deviceTokenService.createDeviceToken(deviceTokenRequest));
      console.log('Device token registered with backend:', result);
      
      return result;
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (this.isNative) {
      return this.nativePushService.disable();
    }
    try {
      const currentStatus = this.subscriptionStatus.value;
      if (!currentStatus.subscription) {
        return true;
      }

      await currentStatus.subscription.unsubscribe();
      
      // Invalidate device tokens on backend
      await this.removeSubscriptionFromBackend();
      
      this.updateStatus({
        isSubscribed: false,
        subscription: null,
        permission: Notification.permission,
        serviceWorkerReady: true,
        vapidConfigured: !!this.config?.vapidPublicKey
      });

      console.log('Successfully unsubscribed from push notifications');
      return true;

    } catch (error) {
      console.error('Error unsubscribing:', error);
      this.updateStatus({
        ...this.subscriptionStatus.value,
        error: `Unsubscribe failed: ${error}`
      });
      return false;
    }
  }

  /**
   * Remove subscription from backend by invalidating device tokens
   */
  private async removeSubscriptionFromBackend(): Promise<void> {
    try {
      // Get all device tokens and invalidate web ones
      const tokenList = await firstValueFrom(this.deviceTokenService.getDeviceTokens());
      
      const webTokens = tokenList.device_tokens.filter(token => 
        token.device_type === 'web' && token.is_active
      );
      
      // Invalidate all active web device tokens
      for (const token of webTokens) {
        try {
          await firstValueFrom(this.deviceTokenService.invalidateDeviceToken(token.id));
          console.log('Invalidated device token:', token.id);
        } catch (error) {
          console.warn('Failed to invalidate device token:', token.id, error);
        }
      }
      
      console.log('Device tokens invalidated on backend');
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
      // Don't throw here as local unsubscribe was successful
    }
  }

  /**
   * Send a test notification locally (browser notification)
   */
  async sendTestNotification(message: string): Promise<boolean> {
    try {
      if (Notification.permission !== 'granted') {
        console.warn('Cannot show test notification: permission not granted');
        return false;
      }

      const notification = new Notification('rettX Test Notification', {
        body: message || 'This is a test notification',
        icon: '/assets/icons/icon-blue-192x192.png',
        badge: '/assets/icons/icon-blue-72x72.png',
        tag: 'rettx-test',
        data: {
          test: true,
          timestamp: Date.now()
        }
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      console.log('Test notification sent');
      return true;

    } catch (error) {
      console.error('Error sending test notification:', error);
      return false;
    }
  }

  /**
   * Send message to service worker
   */
  sendMessageToServiceWorker(message: any): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        if (registration.active) {
          registration.active.postMessage(message);
        }
      });
    }
  }

  /**
   * Test service worker notification (directly from SW)
   */
  testServiceWorkerNotification(): void {
    this.sendMessageToServiceWorker({
      type: 'TEST_NOTIFICATION'
    });
  }
  /**
   * Test manual browser notification
   */
  testManualNotification(): void {
    if (Notification.permission === 'granted') {
      const title = this.translate.instant('NOTIFICATIONS.TEST.TITLE') || 'Manual Test';
      const body = this.translate.instant('NOTIFICATIONS.TEST.BODY') || 'This is a manual notification test';
      
      new Notification(title, {
        body: body,
        icon: 'assets/icons/icon-blue-192x192.png'
      });
    } else {
      console.warn('Notification permission not granted');
    }
  }

  /**
   * Force service worker update
   */
  forceServiceWorkerUpdate(): void {
    this.sendMessageToServiceWorker({
      type: 'SKIP_WAITING'
    });
  }

  /**
   * Set language in service worker for translated notification actions
   */
  setServiceWorkerLanguage(language: string): void {
    this.sendMessageToServiceWorker({
      type: 'SET_LANGUAGE',
      language: language
    });
  }

  /**
   * Deep-link to the OS notification settings for this app. Only meaningful on
   * the native shell (used by the iOS "notifications denied" dead-end UX); a
   * no-op on the web, where the blocked state shows browser-specific steps.
   */
  async openAppSettings(): Promise<void> {
    if (this.isNative) {
      await this.nativePushService.openAppSettings();
    }
  }

  /**
   * Get current subscription status
   */
  getCurrentStatus(): SubscriptionStatus {
    return this.subscriptionStatus.value;
  }

  /**
   * Get current subscription data for debugging
   */
  getCurrentSubscriptionData(): PushSubscriptionData | null {
    const subscription = this.subscriptionStatus.value.subscription;
    if (!subscription) return null;
    
    try {
      return this.extractSubscriptionData(subscription);
    } catch (error) {
      console.error('Error extracting subscription data:', error);
      return null;
    }
  }

  /**
   * Check if push notifications are supported
   */
  isPushSupported(): boolean {
    if (this.isNative) {
      return true;
    }
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
  }

  /**
   * Refresh subscription status
   */
  async refreshStatus(): Promise<void> {
    if (this.isNative) {
      this.bindNativeStatus();
      return;
    }
    await this.checkCurrentSubscription();
  }
  /**
   * Get comprehensive diagnostic information for debugging
   */
  async getDiagnosticInfo(): Promise<{
    browserCapabilities: {
      serviceWorkerSupported: boolean;
      pushManagerSupported: boolean;
      notificationSupported: boolean;
    };
    configuration: {
      vapidPublicKey: string;
      backendUrl: string;
    };
    serviceWorker: {
      registrationState: string;
      scriptUrl?: string;
      updateFound?: boolean;
    };
    subscription: {
      endpoint: string;
      keys: {
        auth: string;
        p256dh: string;
      };
    } | null;
    status: {
      supported: boolean;
      permission: NotificationPermission;
      subscribed: boolean;
      serviceWorkerReady: boolean;
      vapidConfigured: boolean;
    };
  }> {
    const status = this.subscriptionStatus.value;
    const subscriptionData = this.getCurrentSubscriptionData();
    
    // Get service worker information
    let serviceWorkerInfo = {
      registrationState: 'unknown',
      scriptUrl: undefined as string | undefined,
      updateFound: false
    };

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration) {
          serviceWorkerInfo.registrationState = registration.active ? 'active' : 
                                               registration.installing ? 'installing' : 
                                               registration.waiting ? 'waiting' : 'unknown';
          serviceWorkerInfo.scriptUrl = registration.active?.scriptURL;
          serviceWorkerInfo.updateFound = !!registration.waiting;
        }
      }
    } catch (error) {
      console.warn('Error getting service worker info:', error);
      serviceWorkerInfo.registrationState = 'error';
    }

    return {
      browserCapabilities: {
        serviceWorkerSupported: 'serviceWorker' in navigator,
        pushManagerSupported: 'PushManager' in window,
        notificationSupported: 'Notification' in window
      },
      configuration: {
        vapidPublicKey: this.config?.vapidPublicKey ? 
          `${this.config.vapidPublicKey.substring(0, 20)}...` : 'Not configured',
        backendUrl: this.config?.backendUrl || 'Not configured'
      },
      serviceWorker: serviceWorkerInfo,
      subscription: subscriptionData ? {
        endpoint: subscriptionData.endpoint,
        keys: {
          auth: subscriptionData.keys.auth,
          p256dh: subscriptionData.keys.p256dh
        }
      } : null,
      status: {
        supported: this.isPushSupported(),
        permission: status.permission,
        subscribed: status.isSubscribed,
        serviceWorkerReady: status.serviceWorkerReady || false,
        vapidConfigured: status.vapidConfigured || false
      }
    };
  }

  private updateStatus(status: SubscriptionStatus): void {
    this.subscriptionStatus.next(status);
  }

  /**
   * On native, subscribe (once) to NativePushService and project its state into
   * the web-shaped SubscriptionStatus the shared UI already binds to. Readiness
   * flags (`serviceWorkerReady`/`vapidConfigured`) are reported as ready on
   * native since the FCM stack has no service worker or VAPID key — this keeps
   * `canEnableNotifications` and the status messages sensible.
   */
  private bindNativeStatus(): void {
    if (!this.isNative || this.nativeStatusBound) {
      return;
    }
    this.nativeStatusBound = true;
    this.nativePushService.status$.subscribe(native => {
      this.updateStatus(this.mapNativeStatus(native));
    });
  }

  private mapNativeStatus(native: NativePushStatus): SubscriptionStatus {
    return {
      isSubscribed: native.registered,
      subscription: null,
      permission: this.mapNativePermission(native.permission),
      serviceWorkerReady: native.supported,
      vapidConfigured: native.supported,
      error: native.error
    };
  }

  private mapNativePermission(permission: NativePushStatus['permission']): NotificationPermission {
    if (permission === 'granted') {
      return 'granted';
    }
    if (permission === 'denied') {
      return 'denied';
    }
    return 'default';
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  /**
   * Utility: Convert ArrayBuffer to Base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}
